// Prefixes with /employee
const withMySpacePrefix = (route: string) =>
  `/employee${route.startsWith("/") ? "" : "/"}${route}`;
// Prefixes with /company
const withCompanyPrefix = (route: string) =>
  `/company${route.startsWith("/") ? "" : "/"}${route}`;

// ============= AUTH ================= //
export const AUTH_LOGIN = `/auth/login`;
export const AUTH_LOGOUT = "/auth/logout";
export const AUTH_SIGN_UP = `/auth/signup`;
export const AUTH_RESEND_OTP = `/auth/resend-otp`;
export const AUTH_VERIFY_EMAIL = `/auth/verify-email`;
export const AUTH_GET_DEVICES = "/auth/device-sign-ins";
export const AUTH_RESET_PASSWORD = `/auth/reset-password`;
export const AUTH_VALIDATE_RESET_TOKEN = `/auth/validate-reset-token`;
export const AUTH_FORGOT_PASSWORD = `/auth/forgot-password`;
export const AUTH_ACTIVE_SESSIONS = "/auth/sessions";
export const AUTH_VERIFY_RESET_PASSWORD_OTP = "/auth/verify-reset-password-otp";
export const AUTH_VERIFY_MFA_LOGIN = `/auth/verify-mfa-login`;
export const AUTH_TERMINATE = "/auth/sessions/terminate";
export const AUTH_TERMINATE_OTHERS = "auth/sessions/terminate-others";
export const AUTH_LOGIN_HISTORY = "/auth/sessions/login-history";
export const AUTH_EXCHANGE_IMPERSONATION_CODE =
  "/auth/impersonation/exchange-code";
export const AUTH_END_IMPERSONATION = "/auth/impersonation/end";
export const AUTH_REFRESH = "/auth/refresh";

// ============= HEALTH ================= //
// Public, no-auth reachability probe — used by the "connection lost" screen.
export const API_HEALTH = "/health";

// ============= HOME ================= //

// CREATE COMPANY
export const CREATE_COMPANY = "/company";
export const CREATE_COMPANY_INDUSTRY_TYPES =
  withCompanyPrefix("/industry-types");
export const SWITCH_COMPANY = (companyId: number) =>
  withCompanyPrefix(`/switch/${companyId}`);

// USER
export const MS_GET_USER_PROFILE = "/auth/profile";
export const MS_USER_ROLES_ME = "/user-roles/me";
// Self-scoped permission matrix for the current user (JWT-only, no settings guard).
// This is the bootstrap source for usePermissions() — it must NOT require any
// permission of its own, otherwise a user cannot look up their own permissions.
export const MS_MY_PERMISSIONS = "/company/permissions/mine";
export const MS_PROFILE_SETTINGS_INFO = withMySpacePrefix(
  "/users/profile-settings-info",
);
export const MS_EMPLOYMENT_INFORMATION = withMySpacePrefix(
  "/users/employment-info",
);
export const MS_UPLOAD_PROFILE_IMAGE = "/users/upload-profile-image";
export const MS_BANKING_DETAILS = withMySpacePrefix("/users/banking-details");
export const MS_COMPENSATION = withMySpacePrefix("/users/compensation");
export const MS_UPDATE_PASSWORD = withMySpacePrefix("/users/update-password");
export const MS_USER_CUSTOM_STATUS = withMySpacePrefix("/users/custom-status");
export const MS_TIME_ATTENDANCE_RULES = withCompanyPrefix(
  "/time-attendance-employee-rules",
);

// COUNTRIES
export const COUNTRIES_ALL = "/countries/all";
export const NATIONALITY_ALL = "/countries/nationalities";

// NOTIFICATION SETTINGS
export const NOTIFICATION_SETTINGS = "notification-settings";

// SECURITY SETTINGS
export const INITIATE_MFA_SETUP = withMySpacePrefix(
  "/users/mfa/initiate-setup",
);
export const VERIFY_MFA_CODE = withMySpacePrefix("/users/mfa/enable");
export const MFA_STATUS = withMySpacePrefix("/users/mfa/status");
export const DISABLE_MFA = withMySpacePrefix("/users/mfa/disable");

// COMPANIES
export const COMPANY_MINE = "/company/mine";
export const EMPLOYEE_COMPANY_CURRENT = "/employee/company/current";

// TIME ATTENDANCE
export const TIME_ATTENDANCE_REPORT = "/time-attendance/report";
export const TIME_ATTENDANCE_STATS = "/time-attendance/stats";
export const TIME_ATTENDANCE_CLOCK_IN = "/time-attendance/clock-in";
export const TIME_ATTENDANCE_CLOCK_OUT = "/time-attendance/clock-out";
export const TIME_ATTENDANCE_SUMMARY = "/time-attendance/summary";
export const TIME_ATTENDANCE_DRAWER = "/time-attendance/drawer";
export const TIME_ATTENDANCE_BY_DATE = "/time-attendance/by-date";

// ATTENDANCE REGULARIZATION
export const COMPANY_ATTENDANCE_REGULARIZATION = withCompanyPrefix(
  "/attendance-regularization",
);
export const ATTENDANCE_REGULARIZATION = "/attendance-regularization";
export const PAGINATED_ATTENDANCE_REGULARIZATION =
  "/attendance-regularization/paginated";
// Effective regularization policy for the signed-in employee (window + toggles).
export const ATTENDANCE_REGULARIZATION_POLICY =
  "/attendance-regularization/policy";
// Company-level regularization policy (Time & Attendance settings tab).
export const COMPANY_REGULARIZATION_SETTINGS = withCompanyPrefix(
  "/settings/regularization",
);

// LEAVE REQUEST MANAGEMENT
export const API_COMPANY_LEAVE_REQUEST_PAGINATED = withCompanyPrefix(
  "/leave-requests/paginated",
);
export const API_MS_LEAVE_BALANCE_STATS = withMySpacePrefix(
  "/leave-requests/balances",
);
export const API_MS_GET_ALL_LEAVE_REQUESTS = withMySpacePrefix(
  "/leave-requests/paginated",
);
export const API_MS_CREATE_LEAVE_REQUEST = withMySpacePrefix("/leave-requests");
export const API_MS_LEAVE_REQUEST_TYPES = withMySpacePrefix("/leave-types");
export const API_COMPANY_LEAVE_REQUEST_TYPES = withCompanyPrefix("/leave-type");
export const API_MS_LEAVE_REQUEST_SUMMARY = withMySpacePrefix(
  "/leave-requests/summary",
);
export const API_MS_LEAVE_POLICY = withMySpacePrefix("/leave-requests/policy");
export const API_COMPANY_LEAVE_POLICY = withCompanyPrefix(
  "/leave-requests/policy",
);

// COMPANY FILES
export const API_COMPANY_FILES = withCompanyPrefix("/files");
export const API_COMPANY_FILES_UPLOAD = withCompanyPrefix("/files/upload");
export const API_COMPANY_FILE_META = (fileId: string) =>
  withCompanyPrefix(`/files/meta/${encodeURIComponent(fileId)}`);
export const API_COMPANY_FILE_PRESIGNED = (fileId: string) =>
  withCompanyPrefix(`/files/${encodeURIComponent(fileId)}`);
export const API_COMPANY_FILE_RENAME = (fileId: string) =>
  withCompanyPrefix(`/files/${encodeURIComponent(fileId)}/rename`);
export const API_COMPANY_FILE_DELETE = (fileId: string) =>
  withCompanyPrefix(`/files/${encodeURIComponent(fileId)}`);

// ASSETS MANAGEMENT: MY SPACE
export const API_MS_GET_ALL_ASSETS = withMySpacePrefix("/assets");
export const API_MS_ASSET_REQUESTS = withMySpacePrefix("/asset-requests");
export const API_MS_ASSET_REQUEST_STATS = withMySpacePrefix(
  "/asset-requests/stats",
);
export const API_MS_GET_ASSET_REQUEST_DETAILS_BY_ID = (requestId: number) =>
  withMySpacePrefix(`/asset-requests/${requestId}`);
export const API_MS_ASSETS_STATS = withMySpacePrefix("/assets/stats");
export const API_MS_ASSET_HISTORY = withMySpacePrefix("/assets/history");
export const API_MS_CANCEL_ASSET_REQUEST = (requestId: number) =>
  withMySpacePrefix(`/asset-requests/${requestId}/cancel`);
export const API_MS_GET_CONSUMABLES_BY_CATEGORY = (category: string) =>
  `/employee/consumables?consumableCategoryId=${category}`;
export const API_MS_GET_ALL_ASSET_BRANDS = withMySpacePrefix("/brands/all");

// ASSETS MANAGEMENT: COMPANY
export const API_COMPANY_GET_ASSET_STATS = withCompanyPrefix("/assets/stats");
export const API_COMPANY_GET_ASSETS_INVENTORY =
  withCompanyPrefix("/assets/inventory");
export const API_COMPANY_GET_ASSETS_BY_TYPE = withCompanyPrefix("/assets/get");
export const API_COMPANY_ASSET_HISTORY_BY_EMPLOYEE = (userId: number) =>
  withCompanyPrefix(`/assets/employee/${userId}/history`);
export const API_COMPANY_GET_ASSET_DETAILS_BY_ID = (assetId: number) =>
  withCompanyPrefix(`/assets/by-id/${assetId}`);
export const API_COMPANY_CREATE_NEW_ASSET = withCompanyPrefix("/assets");
export const API_COMPANY_UPDATE_ASSET = (assetId: number) =>
  withCompanyPrefix(`/assets/${assetId}`);
export const API_COMPANY_RETIRE_ASSET = (assetId: number) =>
  withCompanyPrefix(`/assets/${assetId}/retired`);
export const API_COMPANY_RESTORE_ASSET = (assetId: number) =>
  withCompanyPrefix(`/assets/${assetId}/restore`);
export const API_COMPANY_DELETE_ASSET = (assetId: number) =>
  withCompanyPrefix(`/assets/${assetId}`);
export const API_COMPANY_GET_ALL_ASSET_TYPES =
  withCompanyPrefix("/asset-types/all");
export const API_COMPANY_GET_ALL_ASSET_BRANDS =
  withCompanyPrefix("/brands/all");
export const API_COMPANY_UPDATE_ASSET_PRIMARY_IMAGE = (
  newPrimaryImageId: number,
) => withCompanyPrefix(`/assets/${newPrimaryImageId}/change-primary`);
export const API_COMPANY_DELETE_ASSET_IMAGE = (imageId: number) =>
  withCompanyPrefix(`/assets/${imageId}/remove-image`);
export const API_COMPANY_GET_ASSETS_IMAGES = (assetId: number) =>
  withCompanyPrefix(`/assets/${assetId}/asset-images`);
export const API_COMPANY_REASSIGN_ASSET = (assetId: number) =>
  withCompanyPrefix(`/assets/${assetId}/assign`);
export const API_COMPANY_UNASSIGN_ASSET = (assetId: number) =>
  withCompanyPrefix(`/assets/${assetId}/unassign`);
export const API_COMPANY_GET_ALL_CONSUMABLE_ASSETS =
  withCompanyPrefix("/consumables/get");
export const API_COMPANY_CREATE_CONSUMABLE_ASSET =
  withCompanyPrefix("/consumables");
export const API_COMPANY_GET_CONSUMABLE_ASSET_BY_ID = (assetId: number) =>
  withCompanyPrefix(`/consumables/by-id/${assetId}`);
export const API_COMPANY_UPDATE_CONSUMABLE_ASSET = (assetId: number) =>
  withCompanyPrefix(`/consumables/${assetId}`);
export const API_COMPANY_DELETE_CONSUMABLE_ASSET = (assetId: number) =>
  withCompanyPrefix(`/consumables/${assetId}`);
export const API_COMPANY_GET_ALL_ASSET_REQUESTS =
  withCompanyPrefix("/asset-requests");
export const API_COMPANY_GET_ASSET_REQUEST_STATS = withCompanyPrefix(
  "/asset-requests/stats",
);
export const API_COMPANY_APPROVE_ASSET_REQUEST = (requestId: number) =>
  withCompanyPrefix(`/asset-requests/${requestId}/approve`);
export const API_COMPANY_REJECT_ASSET_REQUEST = (requestId: number) =>
  withCompanyPrefix(`/asset-requests/${requestId}/reject`);
export const API_COMPANY_GET_ASSET_REQUEST_DETAILS_BY_ID = (
  requestId: number,
) => withCompanyPrefix(`/asset-requests/${requestId}`);
export const API_COMPANY_GET_CONSUMABLE_ASSET_CATEGORY = withCompanyPrefix(
  "/consumables/categories",
);
export const API_COMPANY_GET_CONSUMABLE_UNIT_TYPES = withCompanyPrefix(
  "/consumables/unit-types",
);
export const API_COMPANY_GET_CONSUMABLE_ASSIGNEES = (requestId: number) =>
  withCompanyPrefix(`/consumables/${requestId}/issuances`);

// ASSETS MANAGEMENT: MY-SPACE
export const API_MS_REQUEST_ASSET_REPLACEMENT = (assetId: number) =>
  `/employee/assets/${assetId}/request-replacement`;
export const API_MS_REPORT_ASSET_ISSUE = (assetId: number) =>
  `/employee/assets/${assetId}/report-issue`;
export const API_MS_GET_ALL_ASSET_TYPES = withMySpacePrefix("/asset-types/all");
export const API_MS_GET_CONSUMABLE_ASSET_CATEGORY = withMySpacePrefix(
  "/consumables/categories",
);

// SETTINGS: COMPANY
export const API_COMPANY_INFORMATION = withCompanyPrefix("/current");
export const API_COMPANY_GET_INDUSTRY_TYPES =
  withCompanyPrefix("/industry-types");

export const APIEndpoints = Object.freeze({
  AI: {
    InfoExtractor: withCompanyPrefix("/ai-assistant/info-extractor"),
    InfoAIExtractor: withMySpacePrefix("/ai-assistant/info-extractor"),
    PublicSharedChat: (token: string) => `/public/ai-share/${token}`,
  },
  Common: {
    FileUpload: withCompanyPrefix("/files/upload-temporary"),
    // Tasks attachments controller is mounted at the root (no scope prefix).
    TaskAttachmentUpload: "/tasks/attachments",
    // Files sent in chat, copied into attachments for a ticket made from chat.
    TaskAttachmentFromChat: "/tasks/attachments/from-chat",
  },
  MySpace: {
    Events: {
      GetAllEvents: withMySpacePrefix("/events/invitations"),
      GetEventStats: withMySpacePrefix("/events/invitations/stats"),
      GetEventById: (eventId: string) =>
        withMySpacePrefix(`/events/invitations/${eventId}`),
      GetEventsCalendar: withMySpacePrefix(`/events/invitations/calendar`),
      AcceptEventInvitation: (invitationId: string) =>
        withMySpacePrefix(`/events/${invitationId}/accept`),
      RejectEventInvitation: (invitationId: string) =>
        withMySpacePrefix(`/events/${invitationId}/reject`),
      GetUpcomingEvents: withMySpacePrefix(`/events/invitations/upcoming`),
    },
    AIAssistant: {
      // SmartChat: withMySpacePrefix("/ai-assistant/chat"),
      SmartChat: withMySpacePrefix("/ai-assistant/chat-stream"),
      GetConversations: withMySpacePrefix("/ai-assistant/conversations"),
      GetConversationsWithQuestions: withMySpacePrefix(
        "/ai-assistant/conversations-with-questions",
      ),
      GetQuestionsBySessionId: withMySpacePrefix(
        `/ai-assistant/questions-by-session`,
      ),
      PinConversation: withMySpacePrefix("/ai-assistant/pin-conversation"),
      RenameConversation: withMySpacePrefix("/ai-assistant/rename-title"),
      EditMessage: withMySpacePrefix("/ai-assistant/edit-message"),
      DeleteConversation: withMySpacePrefix("/ai-assistant/conversations"),
      GetSingleConversationHistory: withMySpacePrefix("/ai-assistant/history"),
      DownloadMessagePdf: (messageId: number) =>
        withMySpacePrefix(`/ai-assistant/messages/${messageId}/download-pdf`),
      RetryMessage: withMySpacePrefix("/ai-assistant/retry-message-stream"),
      SpeechToText: withMySpacePrefix("/ai-assistant/speech-to-text"),
      MayaFileUpload: withMySpacePrefix("/ai-assistant/chat-attachment"),
      ShareConversation: (sessionId: string) =>
        withMySpacePrefix(`/ai-assistant/conversations/${sessionId}/share`),
    },
    CompanyPolicies: {
      GetAllCompanyPolicies: withMySpacePrefix("/company-policies"),
      GetCompanyPoliciesStats: withMySpacePrefix("/company-policies/stats"),
      GetCompanyPoliciesById: (companyPoliciesId: string) =>
        withMySpacePrefix(`/company-policies/${companyPoliciesId}`),
      AcknowledgeCompanyPolicies: (companyPoliciesId: string) =>
        withMySpacePrefix(`/company-policies/${companyPoliciesId}/acknowledge`),
    },
    Teams: {
      GetMyTeams: withMySpacePrefix("/teams/my-teams"),
      GetTeamStats: withMySpacePrefix("/teams/stats"),
      GetTeamMembers: (teamId: number) =>
        withMySpacePrefix(`/teams/${teamId}/members`),
    },
    TimeAttendance: {
      UpdateQuickActivityLog: (id: number) =>
        withMySpacePrefix(`/time-attendance/quick-activity/${id}`),
      DeleteTimeAttendanceById: (id: number) => `/time-attendance/${id}`,
      AttendanceExceptions: {
        GetAllAttendanceExceptions: withMySpacePrefix("/attendance-exceptions"),
        GetAttendanceExceptionById: (id: number) =>
          withMySpacePrefix(`/attendance-exceptions/${id}`),
        CreateAttendanceException: withMySpacePrefix("/attendance-exceptions"),
        CancelAttendanceExceptionRequest: (id: number) =>
          withMySpacePrefix(`/attendance-exceptions/${id}/cancel-request`),
        GetAttendanceExceptionLocationCondition: withMySpacePrefix(
          "/attendance-exceptions/location-condition",
        ),
        checkAttendanceExceptionEligibility: withMySpacePrefix(
          "/attendance-exceptions/toggle-clock/location-eligibility",
        ),
      },
    },
    performance: {
      getEvaluationStatistics: withMySpacePrefix("/performance/my-stats"),
      getEvalutaionCycles: withMySpacePrefix("/performance/evaluation-cycles"),
      evaluationCyclesScoreById: (cyclesId: number) =>
        withMySpacePrefix(`/performance/evaluation-request/${cyclesId}/scores`),
      getEvalutaionRequest: withMySpacePrefix(
        `/performance/my-evaluation-requests`,
      ),
      getevaluatonRequestDetailsById: (requestId: number) =>
        withMySpacePrefix(`/performance/evaluation-request/${requestId}`),
    },
    Assets: {
      GetAssetRequestById: (requestId: number) =>
        withMySpacePrefix(`/asset-requests/${requestId}`),
    },
    QuickActivity: {
      GetAll: withMySpacePrefix("/quick-activity"),
    },
    workflow: {
      getWorkflowRequests: withCompanyPrefix("/workflows/instances/all"),
      getAllWorkflowRequests: withCompanyPrefix(
        "/workflows/instances/requests",
      ),
      getWorkflowInstanceStats: withCompanyPrefix("/workflows/instances/stats"),
      getWorkflowInstanceAudit: (
        instanceId: number | string,
        opts?: {
          includeErrors?: boolean;
          eventTypes?: string[];
          actorTypes?: string[];
        },
      ) => {
        const base = withCompanyPrefix(
          `/workflows/instances/${instanceId}/audit`,
        );
        const params: string[] = [];
        if (opts?.includeErrors) params.push("includeErrors=true");
        if (opts?.eventTypes?.length)
          params.push(`eventTypes=${opts.eventTypes.join(",")}`);
        if (opts?.actorTypes?.length)
          params.push(`actorTypes=${opts.actorTypes.join(",")}`);
        return params.length ? `${base}?${params.join("&")}` : base;
      },
      approveWorkflowRequest: (requestId: number) =>
        withCompanyPrefix(`/workflows/instances/${requestId}/approve`),
      rejectWorkflowRequest: (requestId: number) =>
        withCompanyPrefix(`/workflows/instances/${requestId}/reject`),
      requestChangesWorkflow: (requestId: number) =>
        withCompanyPrefix(`/workflows/instances/${requestId}/request-changes`),
      resubmitWorkflow: (requestId: number) =>
        `/employee/workflows/instances/${requestId}/resubmit`,
      deleteWorkflowTemplate: (requestId: string) =>
        `/workflows/templates/${requestId}`,
      previewApplicableWorkflow: "/employee/workflows/instances/preview",
      previewApplicableWorkflowForEmployee: withCompanyPrefix(
        "/workflows/instances/preview",
      ),
      getWorkflowInstanceByEntity: (
        entityType: string,
        entityId: number | string,
      ) =>
        withCompanyPrefix(
          `/workflows/instances/entity/${entityType}/${entityId}`,
        ),
    },
    Tasks: {
      // Projects whose teams I'm a member of.
      MyProjects: withMySpacePrefix("/tasks/projects"),
      ProjectBoard: (projectId: number | string) =>
        withMySpacePrefix(`/tasks/projects/${projectId}/board`),
      // Columns / members of any board the caller can access.
      BoardColumnsByBoard: (boardId: number | string) =>
        withMySpacePrefix(`/tasks/boards/${boardId}/columns`),
      BoardMembersByBoard: (boardId: number | string) =>
        withMySpacePrefix(`/tasks/boards/${boardId}/members`),
      Tasks: withMySpacePrefix("/tasks"),
      // Create many tasks from a JSON file (multipart; dryRun=true previews).
      BulkCreateTasks: withMySpacePrefix("/tasks/bulk-create"),
      TaskById: (taskId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}`),
      MoveTask: (taskId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/move`),
      TaskLinks: (taskId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/links`),
      TaskLinkById: (taskId: number | string, linkId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/links/${linkId}`),
      TaskComments: (taskId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/comments`),
      TaskCommentById: (taskId: number | string, commentId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/comments/${commentId}`),
      TaskWatch: (taskId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/watch`),
      TaskArchive: (taskId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/archive`),
      TaskUnarchive: (taskId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/unarchive`),
      Worklogs: (taskId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/worklogs`),
      WorklogById: (taskId: number | string, worklogId: number | string) =>
        withMySpacePrefix(`/tasks/${taskId}/worklogs/${worklogId}`),
    },
  },
  Company: {
    AuditLogs: {
      Root: withCompanyPrefix("/audit-logs"),
      ById: (id: string | number) => withCompanyPrefix(`/audit-logs/${id}`),
      Filters: withCompanyPrefix("/audit-logs/filters"),
      Stats: withCompanyPrefix("/audit-logs/stats"),
      Export: withCompanyPrefix("/audit-logs/export"),
      Settings: withCompanyPrefix("/audit-logs/settings"),
    },
    Integrations: {
      Root: withCompanyPrefix("/integrations"),
      Available: withCompanyPrefix("/integrations/available"),
      JiraAuthorize: withCompanyPrefix("/integrations/jira/authorize"),
      ApiToken: withCompanyPrefix("/integrations/api-token"),
      Verify: (id: number | string) =>
        withCompanyPrefix(`/integrations/${id}/verify`),
      ById: (id: number | string) => withCompanyPrefix(`/integrations/${id}`),
      JiraProjects: (id: number | string) =>
        withCompanyPrefix(`/integrations/${id}/jira/projects`),
    },
    Tasks: {
      // Boards
      ProjectBoard: (projectId: number | string) =>
        withCompanyPrefix(`/task-boards/project/${projectId}`),
      BoardById: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}`),
      BoardSummary: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/summary`),
      BoardMembers: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/members`),
      BoardImports: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/imports`),
      BoardImportById: (boardId: number | string, importId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/imports/${importId}`),
      BoardImportRun: (boardId: number | string, importId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/imports/${importId}/run`),
      BoardImportPreview: (
        boardId: number | string,
        importId: number | string,
      ) =>
        withCompanyPrefix(
          `/task-boards/${boardId}/imports/${importId}/preview`,
        ),
      BoardReports: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/reports`),
      ReportCreatedVsResolved: (boardId: number | string) =>
        withCompanyPrefix(
          `/task-boards/${boardId}/reports/created-vs-resolved`,
        ),
      ReportEpicBurndown: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/reports/epic-burndown`),
      ReportControlChart: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/reports/control-chart`),
      ReportCfd: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/reports/cfd`),
      SprintReport: (sprintId: number | string) =>
        withCompanyPrefix(`/task-sprints/${sprintId}/report`),
      // Saved filters
      SavedFilters: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/saved-filters`),
      SavedFilterById: (filterId: number | string) =>
        withCompanyPrefix(`/task-boards/saved-filters/${filterId}`),
      // Sprints
      BoardSprints: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/sprints`),
      SprintById: (sprintId: number | string) =>
        withCompanyPrefix(`/task-sprints/${sprintId}`),
      StartSprint: (sprintId: number | string) =>
        withCompanyPrefix(`/task-sprints/${sprintId}/start`),
      CompleteSprint: (sprintId: number | string) =>
        withCompanyPrefix(`/task-sprints/${sprintId}/complete`),
      SprintCompletionSummary: (sprintId: number | string) =>
        withCompanyPrefix(`/task-sprints/${sprintId}/completion-summary`),
      AssignTaskSprint: (taskId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/sprint`),
      // Columns
      BoardColumns: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/columns`),
      ReorderColumns: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/columns/reorder`),
      BoardColumnById: (boardId: number | string, columnId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/columns/${columnId}`),
      // Labels
      BoardLabels: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/labels`),
      BoardLabelById: (boardId: number | string, labelId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/labels/${labelId}`),
      // Backlog drag-to-rank
      BacklogRank: (boardId: number | string) =>
        withCompanyPrefix(`/task-boards/${boardId}/backlog/rank`),
      // Tasks
      Tasks: withCompanyPrefix("/tasks"),
      BulkTasks: withCompanyPrefix("/tasks/bulk"),
      // Create many tasks from a JSON file (multipart; dryRun=true previews).
      BulkCreateTasks: withCompanyPrefix("/tasks/bulk-create"),
      SearchIssues: withCompanyPrefix("/tasks/search"),
      TaskById: (taskId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}`),
      MoveTask: (taskId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/move`),
      TaskLinks: (taskId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/links`),
      TaskLinkById: (taskId: number | string, linkId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/links/${linkId}`),
      TaskComments: (taskId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/comments`),
      TaskCommentById: (taskId: number | string, commentId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/comments/${commentId}`),
      TaskWatch: (taskId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/watch`),
      TaskArchive: (taskId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/archive`),
      TaskUnarchive: (taskId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/unarchive`),
      // Worklogs (time tracking)
      TaskWorklogs: (taskId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/worklogs`),
      TaskWorklogById: (taskId: number | string, worklogId: number | string) =>
        withCompanyPrefix(`/tasks/${taskId}/worklogs/${worklogId}`),
    },
    Events: {
      GetAllEvents: withCompanyPrefix("/events"),
      GetEventStats: withCompanyPrefix("/events/stats"),
      CreateEvent: withCompanyPrefix("/events"),
      UpdateEvent: (eventId: string) => withCompanyPrefix(`/events/${eventId}`),
      GetEventById: (eventId: string | number) =>
        withCompanyPrefix(`/events/${eventId}`),
      GetEventParticipants: (eventId: string) =>
        withCompanyPrefix(`/events/${eventId}/participants`),
      GenerateEventMeetingLink: "/events/virtual-meeting/generate",
      CancelEvent: (eventId: string) =>
        withCompanyPrefix(`/events/${eventId}/cancel`),
      GetEventReport: (eventId: string) =>
        withCompanyPrefix(`/events/${eventId}/report`),
      GetInviteeGroups: withCompanyPrefix("/events/invitee-groups"),
    },
    CompanyPolicies: {
      //Policies
      GetAllCompanyPolicies: withCompanyPrefix("/company-policies/policies"),
      GetCompanyPolicyById: (companyPoliciesId: string) =>
        withCompanyPrefix(`/company-policies/policies/${companyPoliciesId}`),
      CreateCompanyPolicies: withCompanyPrefix("/company-policies/policies"),
      PublishNowCompanyPolicies: withCompanyPrefix(
        "/company-policies/policies/draft",
      ),
      SaveAsDraftCompanyPolicies: withCompanyPrefix(
        "/company-policies/policies/draft",
      ),
      PublishCompanyPolicies: (companyPoliciesId: string) =>
        withCompanyPrefix(
          `/company-policies/policies/${companyPoliciesId}/publish`,
        ),
      DeleteCompanyPolicyById: (companyPoliciesId: string) =>
        withCompanyPrefix(`/company-policies/policies/${companyPoliciesId}`),
      UpdateCompanyPolicyById: (companyPoliciesId: string) =>
        withCompanyPrefix(`/company-policies/policies/${companyPoliciesId}`),
      GetAllCompanyPoliciesAcknowledgmentTracking: (
        companyPoliciesId: string,
      ) =>
        withCompanyPrefix(
          `/company-policies/policies/${companyPoliciesId}/acknowledgment-tracking`,
        ),
      WithdrawCompanyPolicyById: (companyPoliciesId: string) =>
        withCompanyPrefix(
          `/company-policies/policies/${companyPoliciesId}/withdraw`,
        ),

      //Categories
      CreateCompanyPoliciesCategories: withCompanyPrefix(
        "/company-policies/categories",
      ),
      GetAllCompanyPoliciesCategories: withCompanyPrefix(
        "/company-policies/categories",
      ),
      GetCompanyPoliciesCategoryById: (categoryId: string) =>
        withCompanyPrefix(`/company-policies/categories/${categoryId}`),
      UpdateCompanyPoliciesCategory: (categoryId: string) =>
        withCompanyPrefix(`/company-policies/categories/${categoryId}`),
      DeleteCompanyPoliciesCategory: (categoryId: string) =>
        withCompanyPrefix(`/company-policies/categories/${categoryId}`),
    },
    Teams: {
      GetTeams: withCompanyPrefix("/teams/paginated"),
      CreateTeam: withCompanyPrefix("/teams"),
      AssignTeamMembers: (teamId: string) =>
        withCompanyPrefix(`/teams/${teamId}/members`),
      UpdateTeam: (teamId: string) => withCompanyPrefix(`/teams/${teamId}`),
      DeleteTeam: (teamId: string) => withCompanyPrefix(`/teams/${teamId}`),
      GetTeamStats: withCompanyPrefix("/teams/stats"),
      GetTeamRoles: withCompanyPrefix("/settings/teams/roles/paginated"),
      GetTeamRoleById: (roleId: string) =>
        withCompanyPrefix(`/settings/teams/roles/${roleId}`),
      UpdateTeamRole: (roleId: string) =>
        withCompanyPrefix(`/settings/teams/roles/${roleId}`),
      DeleteTeamRole: (roleId: string) =>
        withCompanyPrefix(`/settings/teams/roles/${roleId}`),
      CreateTeamRole: withCompanyPrefix("/settings/teams/roles"),
      GetTeamDetailsAndMembers: (teamId: number) =>
        withCompanyPrefix(`/teams/${teamId}/members`),
      GetTeamDetailsByTeamId: (teamId: number) =>
        withCompanyPrefix(`/teams/${teamId}`),
    },
    TimeAttendance: {
      UpdateQuickActivityLog: (id: number) =>
        withCompanyPrefix(`/time-attendance/quick-activity/${id}`),
      TimeAttendanceRule: {
        DeleteTimeAttendanceRule: (id: number) =>
          withCompanyPrefix(`/time-attendance-employee-rules/${id}`),
      },
      AttendanceExceptions: {
        GetAllAttendanceExceptions: withCompanyPrefix("/attendance-exceptions"),
        GetAttendanceExceptionById: (id: number) =>
          withCompanyPrefix(`/attendance-exceptions/${id}`),
        UpdateAttendanceExceptionStatus: (id: number) =>
          withCompanyPrefix(`/attendance-exceptions/${id}/status`),
      },
    },
    Projects: {
      GetAllProjects: withCompanyPrefix("/projects/paginated"),
      GetProjectStats: withCompanyPrefix("/projects/stats"),
      CreateProject: withCompanyPrefix("/projects"),
      ProjectDetailsById: (projectId: string) =>
        withCompanyPrefix(`/projects/${projectId}`),
      PatchProjectStatus: (projectId: string) =>
        withCompanyPrefix(`/projects/${projectId}/status`),
      Team: {
        AssignProjectTeams: (projectId: string) =>
          withCompanyPrefix(`/projects/${projectId}/assign-teams`),
        GetProjectTeamsByProjectId: (projectId: string) =>
          withCompanyPrefix(`/projects/${projectId}/teams`),
      },
      Attachments: {
        GetProjectAttachments: (projectId: string) =>
          withCompanyPrefix(`/projects/${projectId}/attachments`),
        UploadProjectAttachment: (projectId: string) =>
          withCompanyPrefix(`/projects/${projectId}/attachments`),
        ProjectDocumentEndPointById: (projectId: string, documentId: string) =>
          withCompanyPrefix(`/projects/${projectId}/attachments/${documentId}`),
      },
      KPI: {
        ProjectKPIsEndpoint: (projectId: string) =>
          withCompanyPrefix(`/projects/${projectId}/kpis`),
        KPIIdEndpoint: (projectId: string, kpiId: string) =>
          withCompanyPrefix(`/projects/${projectId}/kpis/${kpiId}`),
      },
      OKR: {
        ProjectOKRsEndpoint: (projectId: string) =>
          withCompanyPrefix(`/projects/${projectId}/okrs`),
        OKRIdEndpoint: (projectId: string, kpiId: string) =>
          withCompanyPrefix(`/projects/${projectId}/okrs/${kpiId}`),
      },
    },
    performance: {
      getTeamPerformance: withCompanyPrefix("/performance/teams"),
      getTeamMembersByTeamId: (teamId: string) =>
        withCompanyPrefix(`/performance/teams/${teamId}/members`),
      getPerformanceCyclesbyMemberId: (memberId: string) =>
        withCompanyPrefix(`/performance/members/${memberId}/evaluation-cycles`),
      getEvaluatorsbyEvaluationId: (evaluationId: string) =>
        withCompanyPrefix(`/performance/evaluations/${evaluationId}/requests`),
      getPerformanceReviewById: (id: string) =>
        withCompanyPrefix(`/performance/evaluation-request/${id}`),
      performanceConfiguration: (teamId: string) =>
        withCompanyPrefix(`/performance/teams/${teamId}/configurations`),
      performanceConfigurationById: (id: string) =>
        withCompanyPrefix(`/performance/configurations/${id}`),
      getTeamStatistics: withCompanyPrefix("/performance/teams/stats"),
      getTeamEvalutaionStats: (teamId: string) =>
        withCompanyPrefix(`/performance/teams/${teamId}/evaluation-stats`),
      getTeamMemberEvalutaionStats: (memberId: string) =>
        withCompanyPrefix(
          `/performance/team-members/${memberId}/evaluation-stats`,
        ),
    },
    settings: {
      performanceMetrics: withCompanyPrefix("/settings/performance/metrics"),
      performanceMetricsById: (metricId: string) =>
        withCompanyPrefix(`/settings/performance/metrics/${metricId}`),
      performanceMetricsPaginated: withCompanyPrefix(
        "/settings/performance/metrics/paginated",
      ),
      updatePerformanceMetricStatus: (metricId: string) =>
        withCompanyPrefix(
          `/settings/performance/metrics/${metricId}/active-status`,
        ),
      getPaginatedLeaveTypes: withCompanyPrefix(
        `/settings/leave-types/paginated`,
      ),
      QuickActivity: {
        GetAll: withCompanyPrefix("/settings/quick-activity"),
        GetPaginated: withCompanyPrefix("/settings/quick-activity/paginated"),
        GetById: (id: number) =>
          withCompanyPrefix(`/settings/quick-activity/${id}`),
        Create: withCompanyPrefix("/settings/quick-activity"),
        Update: (id: number) =>
          withCompanyPrefix(`/settings/quick-activity/${id}`),
        ToggleStatus: (id: number) =>
          withCompanyPrefix(`/settings/quick-activity/${id}/toggle-status`),
        Delete: (id: number) =>
          withCompanyPrefix(`/settings/quick-activity/${id}`),
      },
    },
    AIAssistant: {
      SmartChat: withCompanyPrefix("/ai-assistant/chat-stream"),
      GetConversations: withCompanyPrefix("/ai-assistant/conversations"),
      GetConversationsWithQuestions: withCompanyPrefix(
        "/ai-assistant/conversations-with-questions",
      ),
      GetQuestionsBySessionId: withCompanyPrefix(
        "/ai-assistant/questions-by-session",
      ),
      PinConversation: withCompanyPrefix("/ai-assistant/pin-conversation"),
      RenameConversation: withCompanyPrefix("/ai-assistant/rename-title"),
      EditMessage: withCompanyPrefix("/ai-assistant/edit-message"),
      DeleteConversation: withCompanyPrefix("/ai-assistant/conversations"),
      GetSingleConversationHistory: withCompanyPrefix("/ai-assistant/history"),
      DownloadMessagePdf: (messageId: number) =>
        withCompanyPrefix(`/ai-assistant/messages/${messageId}/download-pdf`),
      RetryMessage: withCompanyPrefix("/ai-assistant/retry-message-stream"),
      SpeechToText: withCompanyPrefix("/ai-assistant/speech-to-text"),
      MayaFileUpload: withCompanyPrefix("/ai-assistant/chat-attachment"),
      ShareConversation: (sessionId: string) =>
        withCompanyPrefix(`/ai-assistant/conversations/${sessionId}/share`),
    },
    Nfc: {
      Forms: {
        List: withCompanyPrefix("/nfc/forms"),
        Create: withCompanyPrefix("/nfc/forms"),
        GetById: (formId: number | string) =>
          withCompanyPrefix(`/nfc/forms/${formId}`),
        Update: (formId: number | string) =>
          withCompanyPrefix(`/nfc/forms/${formId}`),
        Delete: (formId: number | string) =>
          withCompanyPrefix(`/nfc/forms/${formId}`),
        ListAllSubmissions: withCompanyPrefix("/nfc/forms/submissions"),
        ListSubmissions: (formId: number | string) =>
          withCompanyPrefix(`/nfc/forms/${formId}/submissions`),
        GetSubmission: (submissionId: number | string) =>
          withCompanyPrefix(`/nfc/forms/submissions/${submissionId}`),
        UpdateSubmission: (submissionId: number | string) =>
          withCompanyPrefix(`/nfc/forms/submissions/${submissionId}`),
        Clone: (formId: number | string) =>
          withCompanyPrefix(`/nfc/forms/${formId}/clone`),
        SetActive: (formId: number | string) =>
          withCompanyPrefix(`/nfc/forms/${formId}/active`),
      },
      Chips: {
        List: withCompanyPrefix("/nfc/chips"),
        Stats: withCompanyPrefix("/nfc/chips/stats"),
        Analytics: withCompanyPrefix("/nfc/chips/analytics"),
        DeviceTypeCounts: withCompanyPrefix("/nfc/chips/device-type-counts"),
        GetById: (chipId: number | string) =>
          withCompanyPrefix(`/nfc/chips/${chipId}`),
        Events: (chipId: number | string) =>
          withCompanyPrefix(`/nfc/chips/${chipId}/events`),
        Configure: (chipId: number | string) =>
          withCompanyPrefix(`/nfc/chips/${chipId}/configure`),
      },
    },
  },
  PublicNfc: {
    Resolve: (token: string) => `/public/nfc/${token}`,
    Form: (token: string, formId: number | string) =>
      `/public/nfc/${token}/forms/${formId}`,
    UploadFile: (token: string, fieldId: number | string) =>
      `/public/nfc/${token}/fields/${fieldId}/upload`,
    Submit: (token: string) => `/public/nfc/${token}/submit`,
  },
  DigitalId: {
    Me: "/employee/digital-id/me",
    Avatar: "/employee/digital-id/me/avatar",
    Cover: "/employee/digital-id/me/cover",
    Media: "/employee/digital-id/me/media",
    MediaReorder: "/employee/digital-id/me/media/reorder",
    MediaById: (id: number) => `/employee/digital-id/me/media/${id}`,
    ProjectImage: "/employee/digital-id/me/projects/image",
    Publish: "/employee/digital-id/me/publish",
    Submissions: "/employee/digital-id/me/submissions",
    SubmissionById: (id: number) => `/employee/digital-id/me/submissions/${id}`,
    CompanySettings: "/company/digital-id/settings",
    CompanyEmployees: "/company/digital-id/employees",
    CompanyStats: "/company/digital-id/stats",
  },
  PublicDigitalId: {
    Resolve: (id: string | number) => `/public/digital-id/${id}`,
    VCard: (id: string | number) => `/public/digital-id/${id}/vcard`,
    Contact: (id: string | number) => `/public/digital-id/${id}/contact`,
  },
  PublicEvents: {
    GetRsvp: (token: string) => `/public/events/rsvp/${token}`,
    SubmitRsvp: (token: string) => `/public/events/rsvp/${token}`,
  },
  Recruitment: {
    // The link to Jobniq, and the geo reference data it is the authority for.
    // This app has no `cities` table — see the integration notes in CLAUDE.md.
    IntegrationStatus: "/company/recruitment/integration/status",
    IntegrationCountries: "/company/recruitment/integration/countries",
    IntegrationCities: "/company/recruitment/integration/cities",
    Jobs: "/company/recruitment/jobs",
    JobById: (id: number) => `/company/recruitment/jobs/${id}`,
    JobSegmentCounts: "/company/recruitment/jobs/segment-counts",
    JobFilterOptions: "/company/recruitment/jobs/filter-options",
    JobCategories: "/company/recruitment/jobs/categories",
    JobExternalParticipants: "/company/recruitment/jobs/external-participants",
    JobActivity: (id: number) => `/company/recruitment/jobs/${id}/activity`,
    JobInsights: (id: number) => `/company/recruitment/jobs/${id}/insights`,
    JobTemplates: "/company/recruitment/job-templates",
    JobTemplateById: (id: number) => `/company/recruitment/job-templates/${id}`,
    // ── Assessments ────────────────────────────────────────────────────────
    Assessments: "/company/recruitment/assessments",
    AssessmentCounts: "/company/recruitment/assessments/counts",
    AssessmentGenerate: "/company/recruitment/assessments/generate",
    AssessmentById: (id: number) => `/company/recruitment/assessments/${id}`,
    AssessmentDuplicate: (id: number) =>
      `/company/recruitment/assessments/${id}/duplicate`,
    AssessmentArchive: (id: number) =>
      `/company/recruitment/assessments/${id}/archive`,
    AssessmentRestore: (id: number) =>
      `/company/recruitment/assessments/${id}/restore`,
    CandidateAssessments: (candidateId: number) =>
      `/company/recruitment/assessments/candidates/${candidateId}`,
    AssessmentAttemptDetail: (attemptId: number) =>
      `/company/recruitment/assessments/attempts/${attemptId}/detail`,
    AssessmentAttemptReview: (attemptId: number) =>
      `/company/recruitment/assessments/attempts/${attemptId}/review`,
    AssessmentAttemptAcceptAi: (attemptId: number) =>
      `/company/recruitment/assessments/attempts/${attemptId}/accept-ai`,
    AssessmentAttemptAiReview: (attemptId: number) =>
      `/company/recruitment/assessments/attempts/${attemptId}/ai-review`,
    AssessmentAttemptDecision: (attemptId: number) =>
      `/company/recruitment/assessments/attempts/${attemptId}/decision`,
    Applications: "/company/recruitment/applications",
    ApplicationById: (id: number) => `/company/recruitment/applications/${id}`,
    ApplicationStage: (id: number) =>
      `/company/recruitment/applications/${id}/stage`,
    ApplicationScreeningDecision: (id: number) =>
      `/company/recruitment/applications/${id}/screening-decision`,
    ApplicationAiMatch: (id: number) =>
      `/company/recruitment/applications/${id}/ai-match`,
    ApplicationNotes: (id: number) =>
      `/company/recruitment/applications/${id}/notes`,
    ApplicationNoteById: (id: number, noteId: number) =>
      `/company/recruitment/applications/${id}/notes/${noteId}`,
    Candidates: "/company/recruitment/candidates",
    JobPublish: (id: number) => `/company/recruitment/jobs/${id}/publish`,
    JobClose: (id: number) => `/company/recruitment/jobs/${id}/close`,
    JobArchive: (id: number) => `/company/recruitment/jobs/${id}/archive`,
    JobReopen: (id: number) => `/company/recruitment/jobs/${id}/reopen`,
    JobDuplicate: (id: number) => `/company/recruitment/jobs/${id}/duplicate`,
    JobRestore: (id: number) => `/company/recruitment/jobs/${id}/restore`,
    JobNotes: (id: number) => `/company/recruitment/jobs/${id}/notes`,
    JobNoteById: (id: number, noteId: number) =>
      `/company/recruitment/jobs/${id}/notes/${noteId}`,
    ApplicationReject: (id: number) =>
      `/company/recruitment/applications/${id}/reject`,
    ApplicationsAnalysis: "/company/recruitment/applications/analysis",
    ApplicationsCompare: "/company/recruitment/applications/compare",
    ApplicationsAnalysisApprove:
      "/company/recruitment/applications/analysis/approve",
    ApplicationsAddToPipeline:
      "/company/recruitment/applications/add-to-pipeline",
    BulkImportParse: "/company/recruitment/applications/bulk-import/parse",
    BulkImportConfirmPool:
      "/company/recruitment/applications/bulk-import/confirm-pool",
    BulkImportConfirmPipeline:
      "/company/recruitment/applications/bulk-import/confirm-pipeline",
    AiJobPost: "/company/recruitment/jobs/ai/generate",
    AiJobDescription: "/company/recruitment/jobs/ai/description",
    AiJobRequirements: "/company/recruitment/jobs/ai/requirements",
    AiScreeningQuestions: "/company/recruitment/jobs/ai/screening-questions",
    AiInterviewPlan: "/company/recruitment/jobs/ai/interview-plan",
    AiInterviewTeam: "/company/recruitment/jobs/ai/interview-team",
    AiEvaluationCriteria: "/company/recruitment/jobs/ai/evaluation-criteria",
    AiInterviewQuestions: "/company/recruitment/jobs/ai/interview-questions",
    AiJobScore: "/company/recruitment/jobs/ai/job-score",
    Settings: "/company/recruitment/settings",
    CareersTheme: "/company/recruitment/settings/careers-theme",
    CareersThemeLogo: "/company/recruitment/settings/careers-theme/logo",
    CareersThemeLogoIcon: "/company/recruitment/settings/careers-theme/logo-icon",
    CareersThemeBanner: "/company/recruitment/settings/careers-theme/banner",
    Dashboard: "/company/recruitment/dashboard",
    DashboardActivity: "/company/recruitment/dashboard/activity",
    DashboardOverview: "/company/recruitment/dashboard/overview",
    DashboardFunnel: "/company/recruitment/dashboard/funnel",
    DashboardPipeline: "/company/recruitment/dashboard/pipeline",
    TalentPool: "/company/recruitment/talent-pool",
    TalentPoolById: (id: number) => `/company/recruitment/talent-pool/${id}`,
    Referrals: "/company/recruitment/referrals",
    ReferralReviewed: (id: number) =>
      `/company/recruitment/referrals/${id}/reviewed`,
    ReferralReject: (id: number) =>
      `/company/recruitment/referrals/${id}/reject`,
    ReferralAddToPool: (id: number) =>
      `/company/recruitment/referrals/${id}/add-to-pool`,
    ReferralAddToPipeline: (id: number) =>
      `/company/recruitment/referrals/${id}/add-to-pipeline`,
  },
  Interviews: {
    Base: "/company/recruitment/interviews",
    ById: (id: number) => `/company/recruitment/interviews/${id}`,
    /**
     * Opens the video room for a human-conducted round and mints a token.
     *
     * A POST, not a GET: it creates the room with a metered provider on first
     * call and issues a credential on every one.
     */
    Room: (id: number) => `/company/recruitment/interviews/${id}/room`,
    Stats: "/company/recruitment/interviews/stats",
    /**
     * The AI interviewer personas, read live from the interview provider.
     *
     * Reference data, not company data: the same roster for every tenant. It
     * replaced a one-name array in `recruitment.enum.ts` that had to be kept in
     * step with Jobniq's face map by hand.
     */
    AiAvatars: "/company/recruitment/interviews/ai-avatars",
    Reschedule: (id: number) =>
      `/company/recruitment/interviews/${id}/reschedule`,
    Status: (id: number) => `/company/recruitment/interviews/${id}/status`,
    Cancel: (id: number) => `/company/recruitment/interviews/${id}/cancel`,
    Complete: (id: number) => `/company/recruitment/interviews/${id}/complete`,
    Reviewed: (id: number) => `/company/recruitment/interviews/${id}/reviewed`,
    Scorecard: (id: number) =>
      `/company/recruitment/interviews/${id}/scorecard`,
    ScorecardDraft: (id: number) =>
      `/company/recruitment/interviews/${id}/scorecard/draft`,
    AiSession: (id: number) =>
      `/company/recruitment/interviews/${id}/ai-session`,
  },
  Offers: {
    Base: "/company/recruitment/offers",
    Counts: "/company/recruitment/offers/counts",
    ById: (id: number) => `/company/recruitment/offers/${id}`,
    Letter: (id: number) => `/company/recruitment/offers/${id}/letter`,
    Send: (id: number) => `/company/recruitment/offers/${id}/send`,
    Response: (id: number) => `/company/recruitment/offers/${id}/response`,
    Approve: (id: number) => `/company/recruitment/offers/${id}/approve`,
    Handoff: (applicationId: number) =>
      `/company/recruitment/offers/handoff/${applicationId}`,
    Onboard: (applicationId: number) =>
      `/company/recruitment/offers/handoff/${applicationId}/onboard`,
    EarningComponents: "/company/recruitment/offers/earning-components",
    LetterSettings: "/company/recruitment/settings/offer-letters",
    LetterStamp: "/company/recruitment/settings/offer-letters/stamp",
    Signatories: "/company/recruitment/settings/offer-letters/signatories",
    SignatoryById: (id: number) =>
      `/company/recruitment/settings/offer-letters/signatories/${id}`,
  },
  EmployeeReferrals: {
    Base: "/employee/recruitment/referrals",
    Jobs: "/employee/recruitment/referrals/jobs",
  },
});

//EMPLOYEE SETTINGS: COMPANY
export const API_USER_STATUS_UPDATE = withCompanyPrefix("/users");

//NOTIFICATIONS
export const API_GET_NOTIFICATIONS = "/notifications";
export const API_GET_UNREAD_NOTIFICATIONS_COUNT = "/notifications/unread-count";
export const API_GET_NOTIFICATION_STATUS = "/notifications/stats";
export const API_UPDATE_NOTIFICATION_AS_READ = (requestId: number) =>
  `/notifications/${requestId}/toggle`;
export const API_UPDATE_ALL_NOTIFICATION_AS_READ =
  "/notifications/mark-all-read";
export const API_UPDATE_MULTIPLE_NOTIFICATION_AS_READ =
  "/notifications/mark-multiple-read";
export const API_DELETE_NOTIFICATION = (requestId: number) =>
  `/notifications/${requestId}`;
export const API_DELETE_MULTIPLE_NOTIFICATIONS = "/notifications/bulk-delete";
export const API_DELETE_ALL_READ_NOTIFICATIONS =
  "/notifications/delete-all-read";

//ANNOUNCEMENT
export const API_GET_ANNOUNCEMENTS = "/announcements";
export const API_GET_ANNOUNCEMENT_BY_ID = (requestId: number) =>
  `/announcements/${requestId}`;
export const API_UPDATE_ANNOUNCEMENT_AS_READ = (requestId: number) =>
  `/announcements/${requestId}/toggle`;
export const API_UPDATE_ALL_ANNOUNCEMENTS_AS_READ =
  "/announcements/mark-all-read";
export const API_UPDATE_MULTIPLE_ANNOUNCEMENTS_AS_READ =
  "/announcements/mark-multiple-read";
export const API_DELETE_ANNOUNCEMENT = (requestId: number) =>
  `/announcements/${requestId}/delete`;
export const API_BULK_DELETE_ANNOUNCEMENTS = "/announcements/bulk-delete";

//COMPANY ANNOUNCEMENT
export const API_COMPANY_ANNOUNCEMENTS = withCompanyPrefix("/announcements");
export const API_COMPANY_ANNOUNCEMENTS_TYPE = withCompanyPrefix(
  "/announcements/type",
);
export const API_COMPANY_BULK_DELETE_ANNOUNCEMENTS = withCompanyPrefix(
  "/announcements/bulk-delete",
);
export const API_COMPANY_DELETE_ANNOUNCEMENTS_BY_ID = (requestId: number) =>
  withCompanyPrefix(`announcements/${requestId}/delete`);
export const API_COMPANY_ANNOUNCEMENTS_STATUS = withCompanyPrefix(
  "announcements/stats",
);
export const API_COMPANY_USERS_READED_ANNOUNCEMENTS = (requestId: number) =>
  withCompanyPrefix(`announcements/${requestId}/readed-users`);
export const API_COMPANY_GET_ANNOUNCEMENT_BY_ID = (requestId: number) =>
  withCompanyPrefix(`/announcements/get-by-id/${requestId}`);
export const API_COMPANY_UPDATE_ANNOUNCEMENT_STATUS = (requestId: number) =>
  withCompanyPrefix(`/announcements/${requestId}/status`);
export const API_COMPANY_TRIGGER_ANNOUNCEMENT_BY_ID = (requestId: number) =>
  withCompanyPrefix(`/announcements/${requestId}/trigger`);

//SUBSCRIPTION
export const API_GET_SUBSCRIPITON_PLAN = withCompanyPrefix(
  "/subscription-settings/plan",
);
export const API_GET_SUBSCRIPITON_CATEGORIES = withCompanyPrefix(
  "/subscription-settings/benefit-categories",
);
export const API_GET_SUBSCRIPTION_FAQS = withCompanyPrefix(
  "/subscription-settings/faqs",
);
export const API_GET_TOP_UP_TOKENS = withCompanyPrefix(
  "/subscription-settings/token-topup-packages",
);
export const API_ACTIVATE_SUBSCRIPTION_PLAN = withCompanyPrefix(
  "/subscription/activate",
);
export const API_SUBSCRIPTION_GET_CURRENT_PLAN = withCompanyPrefix(
  "/subscription/dashboard/current-plan",
);
export const API_SUBSCRIPTION_GET_TOKEN_SUMMARY = withCompanyPrefix(
  "/subscription/dashboard/tokens-summary",
);
export const API_SUBSCRIPTION_EXPORT_TOKEN_SUMMARY = withCompanyPrefix(
  "/subscription/dashboard/tokens-summary/export",
);
export const API_SUBSCRIPTION_GET_TOKEN_USAGE = withCompanyPrefix(
  "/subscription/dashboard/current-tokens-usage",
);
export const API_SUBSCRIPTION_GET_UPCOMING_INVOICE = withCompanyPrefix(
  "/subscription/dashboard/upcoming-invoice",
);
export const API_CANCEL_SUBSCRIPTION_PLAN = withCompanyPrefix(
  "/subscription/cancel",
);
export const API_RESUME_SUBSCRIPTION_PLAN = withCompanyPrefix(
  "/subscription/resume",
);
export const API_RETRY_SUBSCRIPTION_PAYMENT = withCompanyPrefix(
  "/subscription/retry-payment",
);
export const API_SUBSCRIPTION_SEATS_STATUS = withCompanyPrefix(
  "/subscription/seats",
);
export const API_SUBSCRIPTION_SEATS_UPGRADE = withCompanyPrefix(
  "/subscription/seats/upgrade",
);
export const API_SUBSCRIPTION_SEATS_SCHEDULE_DOWNGRADE = withCompanyPrefix(
  "/subscription/seats/schedule-downgrade",
);
export const API_SUBSCRIPTION_SEATS_CANCEL_SCHEDULED = withCompanyPrefix(
  "/subscription/seats/scheduled-downgrade",
);
export const API_GET_SUBSCRIPTION_PLAN_INVOICES = withCompanyPrefix(
  "/subscription/billing/invoices",
);
export const API_GET_SUBSCRIPTION_BILLING_ADDRESS = withCompanyPrefix(
  "/subscription/billing/address",
);
export const API_UPDATE_SUBSCRIPTION_BILLING_ADDRESS = withCompanyPrefix(
  "/subscription/billing/address",
);
export const API_ADD_NEW_SUBSCRIPTION_PAYMENT_METHOD = withCompanyPrefix(
  "/subscription/billing/payment-methods/setup-intent",
);
export const API_GET_PAYMENT_METHOD_CARD = withCompanyPrefix(
  "/subscription/billing/payment-methods",
);
export const API_SET_DEFAULT_PAYMENT_METHOD = (paymentMethodId: string) =>
  withCompanyPrefix(
    `/subscription/billing/payment-methods/${paymentMethodId}/default`,
  );
export const API_REMOVE_SUBSCRIPTION_PAYMENT_METHOD = (
  paymentMethodId: string,
) =>
  withCompanyPrefix(`/subscription/billing/payment-methods/${paymentMethodId}`);
export const API_GET_SUBSCRIPTION_TOKEN_BALANCE = withCompanyPrefix(
  "/subscription/tokens/balance",
);
export const API_CREATE_TOP_UP_PACKAGE = withCompanyPrefix(
  "/subscription/tokens/topup/intent",
);
export const API_GET_TOP_UP_PACKAGE_BY_ID = (packageId: string) =>
  withCompanyPrefix(`/subscription/tokens/topup-packages/${packageId}`);

// ─── OFFBOARDING ─────────────────────────────────────────────────────────────
export const API_COMPANY_OFFBOARDINGS = withCompanyPrefix("/offboardings");
export const API_COMPANY_OFFBOARDING_BY_ID = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}`);
export const API_COMPANY_OFFBOARDING_CANCEL = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/cancel`);
export const API_COMPANY_OFFBOARDING_INVITATION = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/invitation`);
export const API_COMPANY_OFFBOARDING_SEND_INVITATION = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/invitation/send`);
export const API_COMPANY_OFFBOARDING_ACCEPT_INVITATION = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/invitation/accept`);
export const API_COMPANY_OFFBOARDING_SKIP_INVITATION = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/invitation/skip`);
export const API_COMPANY_OFFBOARDING_ASSET_CLEARANCE = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/asset-clearance`);
export const API_COMPANY_OFFBOARDING_COMPLETE_ASSET_CLEARANCE = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/asset-clearance/complete`);
export const API_COMPANY_OFFBOARDING_DOCUMENTS = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/documents`);
export const API_COMPANY_OFFBOARDING_DOCUMENT_BY_ID = (
  id: number,
  docId: number,
) => withCompanyPrefix(`/offboardings/${id}/documents/${docId}`);
export const API_COMPANY_OFFBOARDING_SEND_FOR_SIGNATURE = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/documents/send-for-signature`);
export const API_COMPANY_OFFBOARDING_COMPLETE_DOCUMENTS = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/documents/complete`);
export const API_COMPANY_OFFBOARDING_SETTLEMENT = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/settlement`);
export const API_COMPANY_OFFBOARDING_SETTLEMENT_PREVIEW = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/settlement/preview`);
export const API_COMPANY_OFFBOARDING_SUBMIT_TO_PAYROLL = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/settlement/submit-to-payroll`);
export const API_COMPANY_OFFBOARDING_SKIP_SETTLEMENT = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/settlement/skip`);
export const API_COMPANY_OFFBOARDING_APPROVERS = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/approvers`);
export const API_COMPANY_OFFBOARDING_REMOVE_APPROVER = (
  id: number,
  approverId: number,
) => withCompanyPrefix(`/offboardings/${id}/approvers/${approverId}`);
export const API_COMPANY_OFFBOARDING_APPROVE = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/approve`);
export const API_COMPANY_OFFBOARDING_REJECT = (id: number) =>
  withCompanyPrefix(`/offboardings/${id}/reject`);
export const API_COMPANY_OFFBOARDING_PENDING_APPROVALS = withCompanyPrefix(
  "/offboardings/pending-approvals",
);
export const API_COMPANY_OFFBOARDING_BLOCKED_EMPLOYEE_IDS = withCompanyPrefix(
  "/offboardings/blocked-employee-ids",
);
export const API_COMPANY_OFFBOARDING_REBOARD = (employeeId: number) =>
  withCompanyPrefix(`/offboardings/employees/${employeeId}/reboard`);

// ─── MY SPACE OFFBOARDING ─────────────────────────────────────────────────────
export const API_MS_OFFBOARDING_MY = withMySpacePrefix("/offboardings/my");
export const API_MS_OFFBOARDING_MY_INVITATION = withMySpacePrefix(
  "/offboardings/my/invitation",
);
export const API_MS_OFFBOARDING_ACCEPT_INVITATION = withMySpacePrefix(
  "/offboardings/my/invitation/accept",
);
export const API_MS_OFFBOARDING_MY_ASSET_CLEARANCE = withMySpacePrefix(
  "/offboardings/my/asset-clearance",
);
export const API_MS_OFFBOARDING_MY_DOCUMENTS = withMySpacePrefix(
  "/offboardings/my/documents",
);
export const API_MS_OFFBOARDING_MY_SETTLEMENT = withMySpacePrefix(
  "/offboardings/my/settlement",
);
export const API_MS_OFFBOARDING_MY_GRATUITY_PREVIEW = withMySpacePrefix(
  "/offboardings/my/settlement/gratuity-preview",
);
export const API_MS_OFFBOARDING_MY_STATUTORY_PREVIEW = withMySpacePrefix(
  "/offboardings/my/settlement/statutory-preview",
);
export const API_MS_OFFBOARDING_APPROVAL_STATUS = withMySpacePrefix(
  "/offboardings/my/approval-status",
);

// ─── ONBOARDING SETTINGS ──────────────────────────────────────────────────────
export const API_COMPANY_DIGITAL_SYSTEMS = withCompanyPrefix(
  "/onboarding-settings/digital-systems",
);
export const API_COMPANY_DIGITAL_SYSTEM_BY_ID = (id: number) =>
  withCompanyPrefix(`/onboarding-settings/digital-systems/${id}`);
export const API_COMPANY_DIGITAL_SYSTEM_TOGGLE = (id: number) =>
  withCompanyPrefix(`/onboarding-settings/digital-systems/${id}/toggle`);
export const API_COMPANY_EMPLOYEE_DIGITAL_ACCESS = (employeeId: number) =>
  withCompanyPrefix(
    `/onboarding-settings/digital-systems/employee-access/${employeeId}`,
  );
export const API_COMPANY_EMPLOYEE_DIGITAL_ACCESS_DELETE = (
  employeeId: number,
  id: number,
) =>
  withCompanyPrefix(
    `/onboarding-settings/digital-systems/employee-access/${employeeId}/${id}`,
  );
export const API_COMPANY_EMPLOYEE_DIGITAL_ACCESS_ALL = (employeeId: number) =>
  withCompanyPrefix(
    `/onboarding-settings/digital-systems/employee-access/${employeeId}/all`,
  );
export const API_MS_MY_DIGITAL_ACCESS = withMySpacePrefix(
  "/onboarding-settings/digital-systems/my-access",
);
export const API_MS_MY_DIGITAL_ACCESS_ALL = withMySpacePrefix(
  "/onboarding-settings/digital-systems/my-access/all",
);
export const API_GET_AI_ACTIONS = withCompanyPrefix(
  "/subscription-settings/ai-actions",
);

// ── Activity / history endpoints ───────────────────────────────────────────
export const API_SUBSCRIPTION_TOKEN_PURCHASES = withCompanyPrefix(
  "/subscription/tokens/purchases",
);
export const API_SUBSCRIPTION_SEAT_HISTORY = withCompanyPrefix(
  "/subscription/seats/history",
);
export const API_SUBSCRIPTION_PAYMENT_HISTORY = withCompanyPrefix(
  "/subscription/billing/payment-history",
);
export const API_SUBSCRIPTION_ACTIVITY = withCompanyPrefix(
  "/subscription/activity",
);
export const API_SUBSCRIPTION_TOKEN_USAGE_BREAKDOWN = withCompanyPrefix(
  "/subscription/dashboard/token-usage-breakdown",
);
export const API_SUBSCRIPTION_MY_TIME_SAVED = withMySpacePrefix(
  "/subscription/my-time-saved",
);
export const API_SUBSCRIPTION_USERS_TIME_SAVED = withCompanyPrefix(
  "/subscription/dashboard/users-time-saved",
);

// ── Platform Config (public) ───────────────────────────────────────────────
export const API_PLATFORM_CONFIG_MAINTENANCE = "/platform-config/maintenance";

// ── EXPENSES: scope-agnostic (auth only) ────────────────────────────────────
export const API_EXPENSE_LOOKUPS = "/expenses/lookups";
export const API_EXPENSE_ATTACHMENTS = "/expenses/attachments";
export const API_EXPENSE_OCR = "/expenses/attachments/ocr";
export const API_EXPENSE_PROJECT_CATEGORY_CAPS = (projectId: number) =>
  `/expenses/lookups/projects/${projectId}/category-caps`;

// ── EXPENSES: MY SPACE ───────────────────────────────────────────────────────
export const API_MS_GET_EXPENSES = withMySpacePrefix("/expenses");
export const API_MS_EXPENSE_STATS = withMySpacePrefix("/expenses/stats");
export const API_MS_GET_EXPENSE_BY_ID = (id: number) =>
  withMySpacePrefix(`/expenses/${id}`);
export const API_MS_CREATE_EXPENSE = withMySpacePrefix("/expenses");
export const API_MS_UPDATE_EXPENSE = (id: number) =>
  withMySpacePrefix(`/expenses/${id}`);
export const API_MS_RESUBMIT_EXPENSE = (id: number) =>
  withMySpacePrefix(`/expenses/${id}/resubmit`);
export const API_MS_DELETE_EXPENSE = (id: number) =>
  withMySpacePrefix(`/expenses/${id}`);
export const API_MS_EXPENSE_COMMENTS = (id: number) =>
  withMySpacePrefix(`/expenses/${id}/comments`);
export const API_MS_GET_REIMBURSEMENTS = withMySpacePrefix("/reimbursements");
export const API_MS_REIMBURSEMENT_STATS = withMySpacePrefix(
  "/reimbursements/stats",
);
export const API_MS_GET_REIMBURSEMENT_BY_ID = (id: number) =>
  withMySpacePrefix(`/reimbursements/${id}`);

// ── EXPENSES: COMPANY ─────────────────────────────────────────────────────────
export const API_COMPANY_GET_EXPENSES = withCompanyPrefix("/expenses");
export const API_COMPANY_EXPENSE_STATS = withCompanyPrefix("/expenses/stats");
export const API_COMPANY_GET_EXPENSE_BY_ID = (id: number) =>
  withCompanyPrefix(`/expenses/${id}`);
export const API_COMPANY_CREATE_EXPENSE = withCompanyPrefix("/expenses");
export const API_COMPANY_UPDATE_EXPENSE = (id: number) =>
  withCompanyPrefix(`/expenses/${id}`);
export const API_COMPANY_DELETE_EXPENSE = (id: number) =>
  withCompanyPrefix(`/expenses/${id}`);
export const API_COMPANY_SUBMIT_EXPENSE = (id: number) =>
  withCompanyPrefix(`/expenses/${id}/submit`);
export const API_COMPANY_EXPENSE_DECISION = (id: number) =>
  withCompanyPrefix(`/expenses/${id}/decision`);
export const API_COMPANY_EXPENSE_COMMENTS = (id: number) =>
  withCompanyPrefix(`/expenses/${id}/comments`);
export const API_COMPANY_EXPENSE_ASSET_SYNC = (id: number) =>
  withCompanyPrefix(`/expenses/${id}/asset-sync`);
export const API_COMPANY_GET_REIMBURSEMENTS =
  withCompanyPrefix("/reimbursements");
export const API_COMPANY_REIMBURSEMENT_STATS = withCompanyPrefix(
  "/reimbursements/stats",
);
export const API_COMPANY_GET_REIMBURSEMENT_BY_ID = (id: number) =>
  withCompanyPrefix(`/reimbursements/${id}`);
export const API_COMPANY_PAY_REIMBURSEMENT = (id: number) =>
  withCompanyPrefix(`/reimbursements/${id}/pay`);
export const API_COMPANY_BULK_PAY_REIMBURSEMENT = withCompanyPrefix(
  "/reimbursements/bulk-pay",
);
export const API_COMPANY_CANCEL_REIMBURSEMENT = (id: number) =>
  withCompanyPrefix(`/reimbursements/${id}/cancel`);
export const API_COMPANY_EXPENSE_PROJECTS =
  withCompanyPrefix("/expense-projects");
export const API_COMPANY_EXPENSE_PROJECT_BY_ID = (id: number) =>
  withCompanyPrefix(`/expense-projects/${id}`);
export const API_COMPANY_EXPENSE_PROJECT_BUDGET = (id: number) =>
  withCompanyPrefix(`/expense-projects/${id}/budget`);
export const API_COMPANY_EXPENSE_PROJECT_BUDGET_HISTORY = (id: number) =>
  withCompanyPrefix(`/expense-projects/${id}/budget-history`);
/** Platform currency catalogue (not company-scoped). */
export const API_CURRENCIES = "/currencies";
export const API_COMPANY_EXPENSE_SETTINGS_CATEGORIES = withCompanyPrefix(
  "/expense-settings/categories",
);
export const API_COMPANY_EXPENSE_SETTINGS_CATEGORY = (id: number) =>
  withCompanyPrefix(`/expense-settings/categories/${id}`);
export const API_COMPANY_EXPENSE_SETTINGS_TYPES = withCompanyPrefix(
  "/expense-settings/types",
);
export const API_COMPANY_EXPENSE_SETTINGS_TYPE = (id: number) =>
  withCompanyPrefix(`/expense-settings/types/${id}`);
export const API_COMPANY_EXPENSE_SETTINGS_METHODS = withCompanyPrefix(
  "/expense-settings/reimbursement-methods",
);
export const API_COMPANY_EXPENSE_SETTINGS_METHOD = (id: number) =>
  withCompanyPrefix(`/expense-settings/reimbursement-methods/${id}`);

// ── SALES & COMMISSIONS: MY SPACE ────────────────────────────────────────────
export const API_MS_GET_SALES = withMySpacePrefix("/sales");
export const API_MS_SALE_STATS = withMySpacePrefix("/sales/stats");
export const API_MS_GET_SALE_BY_ID = (id: number) =>
  withMySpacePrefix(`/sales/${id}`);
export const API_MS_CREATE_SALE = withMySpacePrefix("/sales");
export const API_MS_UPDATE_SALE = (id: number) =>
  withMySpacePrefix(`/sales/${id}`);
export const API_MS_SUBMIT_SALE = (id: number) =>
  withMySpacePrefix(`/sales/${id}/submit`);
export const API_MS_DELETE_SALE = (id: number) =>
  withMySpacePrefix(`/sales/${id}`);
/** The commission plan resolved for the signed-in employee. */
export const API_MS_MY_COMMISSION_PLAN = withMySpacePrefix(
  "/sales/my-commission-plan",
);
/** Preview a commission before the sale is saved. */
export const API_MS_SALE_COMMISSION_PREVIEW = withMySpacePrefix(
  "/sales/commission-preview",
);

// ── SALES & COMMISSIONS: COMPANY ─────────────────────────────────────────────
export const API_COMPANY_GET_SALES = withCompanyPrefix("/sales");
/** Log a sale on behalf of an employee (`performedFor`). */
export const API_COMPANY_CREATE_SALE = withCompanyPrefix("/sales");
export const API_COMPANY_SALE_STATS = withCompanyPrefix("/sales/stats");
export const API_COMPANY_GET_SALE_BY_ID = (id: number) =>
  withCompanyPrefix(`/sales/${id}`);
export const API_COMPANY_SALE_DECISION = (id: number) =>
  withCompanyPrefix(`/sales/${id}/decision`);
/** Override an already-approved sale's commission (amount + mandatory reason). */
export const API_COMPANY_SALE_COMMISSION_OVERRIDE = (id: number) =>
  withCompanyPrefix(`/sales/${id}/commission-override`);
export const API_COMPANY_SALE_COMMISSION_PREVIEW = withCompanyPrefix(
  "/sales/commission-preview",
);
/** Per-employee commission roll-up for the company Commissions tab. */
export const API_COMPANY_COMMISSIONS_BY_EMPLOYEE = withCompanyPrefix(
  "/sales/commissions/by-employee",
);
export const API_COMPANY_COMMISSIONS_SUMMARY = withCompanyPrefix(
  "/sales/commissions/summary",
);

// ── SALES & COMMISSIONS: PLAN SETTINGS (COMPANY) ─────────────────────────────
export const API_COMPANY_COMMISSION_PLANS =
  withCompanyPrefix("/commission-plans");
export const API_COMPANY_COMMISSION_PLAN = (id: number) =>
  withCompanyPrefix(`/commission-plans/${id}`);

// ============= DATA TRANSFER (bulk import / export) ================= //
export const API_COMPANY_DATA_TRANSFER_ENTITIES = withCompanyPrefix(
  "/data-transfer/entities",
);
export const API_COMPANY_DATA_TRANSFER_TEMPLATE = withCompanyPrefix(
  "/data-transfer/template",
);
export const API_COMPANY_DATA_TRANSFER_IMPORTS = withCompanyPrefix(
  "/data-transfer/imports",
);
export const API_COMPANY_DATA_TRANSFER_IMPORT = (runId: number) =>
  withCompanyPrefix(`/data-transfer/imports/${runId}`);
export const API_COMPANY_DATA_TRANSFER_MAPPING = (runId: number) =>
  withCompanyPrefix(`/data-transfer/imports/${runId}/mapping`);
export const API_COMPANY_DATA_TRANSFER_VALIDATE = (runId: number) =>
  withCompanyPrefix(`/data-transfer/imports/${runId}/validate`);
export const API_COMPANY_DATA_TRANSFER_RUN = (runId: number) =>
  withCompanyPrefix(`/data-transfer/imports/${runId}/run`);
export const API_COMPANY_DATA_TRANSFER_CANCEL = (runId: number) =>
  withCompanyPrefix(`/data-transfer/imports/${runId}/cancel`);
export const API_COMPANY_DATA_TRANSFER_UNDO = (runId: number) =>
  withCompanyPrefix(`/data-transfer/imports/${runId}/undo`);
export const API_COMPANY_DATA_TRANSFER_ERRORS = (runId: number) =>
  withCompanyPrefix(`/data-transfer/imports/${runId}/errors`);
export const API_COMPANY_DATA_TRANSFER_SOURCE_FILE = (runId: number) =>
  withCompanyPrefix(`/data-transfer/imports/${runId}/source-file`);
export const API_COMPANY_DATA_TRANSFER_ARTIFACT = (runId: number) =>
  withCompanyPrefix(`/data-transfer/imports/${runId}/artifact`);
export const API_COMPANY_DATA_TRANSFER_EXPORTS = withCompanyPrefix(
  "/data-transfer/exports",
);
export const API_COMPANY_DATA_TRANSFER_EXPORT = (runId: number) =>
  withCompanyPrefix(`/data-transfer/exports/${runId}`);
export const API_COMPANY_DATA_TRANSFER_EXPORT_DOWNLOAD = (runId: number) =>
  withCompanyPrefix(`/data-transfer/exports/${runId}/download`);

// ============= DASHBOARD LAYOUT ================= //
// Not company-prefixed: one endpoint serves both the Workspace and My Space
// canvases, told apart by the `scope` query param.
export const API_DASHBOARD_LAYOUT = "/dashboard/layout";
// ============= COMPANY SETUP GUIDE ================= //
// The company "go-live" guide: tracks a new workspace from subscription through
// to its first payroll run. Distinct from the EMPLOYEE onboarding endpoints
// above (`/company/onboarding-settings/*`), which provision one new hire.
export const API_COMPANY_SETUP_PROGRESS = withCompanyPrefix("/setup/progress");
export const API_COMPANY_SETUP_STEP = (step: string) =>
  withCompanyPrefix(`/setup/steps/${step}`);
export const API_COMPANY_SETUP_RESET = withCompanyPrefix("/setup/reset");
export const API_COMPANY_SETUP_DISMISS = withCompanyPrefix("/setup/dismiss");
export const API_COMPANY_SETUP_RESTORE = withCompanyPrefix("/setup/restore");

// ============= CHAT ================= //
// Employee chat (api.maihrms.com ChatController). Not space-prefixed: every
// employee chats as themselves.
export const API_CHAT_CAPABILITIES = "/chat/capabilities";
export const API_CHAT_UNREAD_COUNT = "/chat/unread-count";
export const API_CHAT_SETTINGS = "/chat/settings";
/** PATCH { kind, keep } — keep or stop one kind of channel notice. */
export const API_CHAT_CHANNEL_NOTICES = "/chat/channel-notices";
/** GET ?q=&before= — messages matching in all my chats. */
export const API_CHAT_SEARCH = "/chat/search";
export const API_CHAT_CONTACTS = "/chat/contacts";
export const API_CHAT_CONVERSATIONS = "/chat/conversations";
export const API_CHAT_DIRECT = "/chat/conversations/direct";
export const API_CHAT_GROUP = "/chat/conversations/group";
export const API_CHAT_BROADCAST = "/chat/conversations/broadcast";
export const API_CHAT_WORK_STATUS = "/chat/conversations/work-status";
export const API_CHAT_WORK_STATUS_RECIPIENTS = "/chat/work-status/recipients";
export const API_CHAT_AUDIENCES = "/chat/audiences";
export const API_CHAT_AUDIENCE_MEMBERS = "/chat/audiences/members";
export const API_CHAT_ATTACHMENTS = "/chat/attachments";
export const API_CHAT_SHORTCUT_ITEMS = "/chat/shortcut-items";
export const API_CHAT_CONVERSATION = (id: number) => `/chat/conversations/${id}`;
export const API_CHAT_CONVERSATION_ACTION = (id: number, action: string) =>
  `/chat/conversations/${id}/${action}`;
export const API_CHAT_MEMBER = (id: number, userId: number) =>
  `/chat/conversations/${id}/members/${userId}`;
export const API_CHAT_MESSAGE = (id: number) => `/chat/messages/${id}`;
export const API_CHAT_MESSAGE_VOTE = (id: number) => `/chat/messages/${id}/vote`;
export const API_CHAT_MESSAGE_PLAYED = (id: number) => `/chat/messages/${id}/played`;
export const API_CHAT_MESSAGE_REACTION = (id: number) => `/chat/messages/${id}/reaction`;
export const API_CHAT_MESSAGE_PIN = (id: number) => `/chat/messages/${id}/pin`;
export const API_CHAT_MESSAGE_FORWARD = (id: number) => `/chat/messages/${id}/forward`;
export const API_CHAT_CONVERSATION_PINS = (id: number) => `/chat/conversations/${id}/pins`;
export const API_CHAT_PINNED_CONVERSATIONS = "/chat/pinned-conversations";
export const API_CHAT_SCHEDULED = (id: number) => `/chat/scheduled/${id}`;
export const API_CHAT_SCHEDULED_SEND = (id: number) => `/chat/scheduled/${id}/send`;

// ============= APPLICATION GUIDES ================= //
// Shared by My Space and Workspace; the `audience` query param picks the space.
export const API_GUIDES = "/guides";
export const API_GUIDES_FILTERS = "/guides/filters";
export const API_GUIDE_BY_SLUG = (slug: string) => `/guides/${encodeURIComponent(slug)}`;
export const API_GUIDE_VIEW = (slug: string) => `/guides/${encodeURIComponent(slug)}/view`;
